export interface Column<T> {
  key: keyof T | string;
  label: string;
  sortKey?: string;
  render?: (item: T) => React.ReactNode;
  /** Override default cell padding/width classes (replaces `px-6 ... whitespace-nowrap`). */
  cellClassName?: string;
  /** Override default header padding classes (replaces `px-6`). */
  headerClassName?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  onRowClick?: (item: T) => void;
  keyExtractor?: (item: T) => string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  /** Use fixed table-layout so columns honour their declared widths and never overflow. */
  fixedLayout?: boolean;
  /** Per-row class override (e.g. background tint for inactive rows). */
  rowClassName?: (item: T) => string | undefined;
  /** Override the fixed-layout min-width (default `900px`). */
  minWidth?: string;
}

function SortIcon({ direction }: { direction: 'asc' | 'desc' | null }) {
  return (
    <svg className="w-3.5 h-3.5 ml-1 inline-block" viewBox="0 0 10 14" fill="none">
      <path
        d="M5 0L9.33 5.25H0.67L5 0Z"
        fill="currentColor"
        className={direction === 'asc' ? 'text-content' : 'text-border-strong'}
      />
      <path
        d="M5 14L0.67 8.75H9.33L5 14Z"
        fill="currentColor"
        className={direction === 'desc' ? 'text-content' : 'text-border-strong'}
      />
    </svg>
  );
}

export function DataTable<T>({
  columns,
  data,
  loading = false,
  onRowClick,
  keyExtractor,
  sortBy,
  sortOrder,
  onSort,
  fixedLayout = false,
  rowClassName,
  minWidth,
}: DataTableProps<T>) {
  if (loading) {
    return (
      <div className="bg-surface rounded-lg border border-border overflow-hidden">
        <div className="animate-pulse">
          {/* Header skeleton */}
          <div className="bg-surface-muted border-b border-border px-6 py-3">
            <div className="flex gap-8">
              {columns.map((_, i) => (
                <div key={i} className="h-4 bg-surface-muted rounded w-20" />
              ))}
            </div>
          </div>
          {/* Row skeletons */}
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="px-6 py-4 border-b border-border last:border-0">
              <div className="flex gap-8">
                {columns.map((_, j) => (
                  <div key={j} className="h-4 bg-surface-muted rounded w-24" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-surface rounded-lg border border-border overflow-hidden">
        <div className="px-6 py-12 text-center">
          <p className="text-sm text-content-subtle">No data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg border border-border overflow-hidden">
      <div className="overflow-x-auto -webkit-overflow-scrolling-touch">
        <table
          className={`w-full ${fixedLayout ? 'md:table-fixed' : ''}`}
          style={fixedLayout ? { minWidth: minWidth || '900px' } : undefined}
        >
          <thead className="bg-surface-muted border-b border-border">
            <tr>
              {columns.map((column) => {
                const colSortKey = column.sortKey;
                const isSortable = !!colSortKey && !!onSort;
                const isActive = colSortKey === sortBy;
                return (
                  <th
                    key={String(column.key)}
                    className={`${column.headerClassName ?? 'px-3 sm:px-6'} py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isSortable
                        ? 'cursor-pointer select-none hover:text-content text-content-subtle'
                        : 'text-content-subtle'
                    } ${isActive ? 'text-content' : ''}`}
                    onClick={isSortable ? () => onSort(colSortKey) : undefined}
                  >
                    <span className="inline-flex items-center">
                      {column.label}
                      {isSortable && (
                        <SortIcon direction={isActive ? (sortOrder || 'desc') : null} />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.map((item, index) => {
              const key = keyExtractor
                ? keyExtractor(item)
                : (item as { id?: string | number }).id != null
                  ? String((item as { id?: string | number }).id)
                  : String(index);

              return (
                <tr
                  key={key}
                  onClick={() => onRowClick?.(item)}
                  className={`${
                    onRowClick
                      ? 'cursor-pointer hover:bg-surface-muted transition-colors'
                      : ''
                  } ${rowClassName?.(item) ?? ''}`}
                >
                  {columns.map((column) => (
                    <td
                      key={String(column.key)}
                      className={
                        column.cellClassName ??
                        'px-3 sm:px-6 py-3 sm:py-4 text-sm text-content whitespace-nowrap'
                      }
                    >
                      {column.render
                        ? column.render(item)
                        : String(item[column.key as keyof T] ?? '')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
